import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./ExportOptionsModal.css";
import { isPlaceholderDoc } from "../../Common/exportTripHTML";



function guideAvailability(guide) {
  if (!guide) return { scheduleNames: [] };
  const places = (guide.places?.placesList || []).filter(
    p => !p.Hidden && p.PType !== "PolyLine" && p.PType !== "Polygon"
  );
  const rawDoc = guide.document || "";
  const scheduleNames = guide.schedule?.geScheduleNames?.()?.map(s => s.label) || [];
  return {
    overallMap: places.length > 0,
    itinerary:  !isPlaceholderDoc(rawDoc),
    places:     places.length > 0,
    schedule:   (guide.schedule?.events?.length || 0) > 0,
    chapters:   (guide.chapters || []).some(ch => (ch.sections || []).some(s => s.html)),
    scheduleNames,
  };
}

const DEFAULT_SECTIONS = [
  { id: "itinerary", label: "Itinerary" },
  { id: "schedule",  label: "Schedule"  },
  { id: "places",    label: "Places"    },
  { id: "chapters",  label: "My Notes"  },
];

export default function ExportOptionsModal({ show, onHide, guide, onExportHTML, onExportPDF }) {
  const [includeHero,       setIncludeHero]       = useState(true);
  const [includeStats,      setIncludeStats]       = useState(true);
  const [includeOverallMap, setIncludeOverallMap]  = useState(true);
  const [sections, setSections] = useState(
    DEFAULT_SECTIONS.map(s => ({ ...s, enabled: false, available: false }))
  );
  const [scheduleNames,         setScheduleNames]         = useState([]);
  const [selectedScheduleNames, setSelectedScheduleNames] = useState([]);

  useEffect(() => {
    if (!show) return;
    const avail = guideAvailability(guide);
    setIncludeHero(true);
    setIncludeStats(true);
    setIncludeOverallMap(avail.overallMap);
    setSections(
      DEFAULT_SECTIONS.map(s => ({
        ...s,
        available: !!avail[s.id],
        enabled:   !!avail[s.id],
      }))
    );
    setScheduleNames(avail.scheduleNames);
    // Default to just "Main" so exports don't balloon with every schedule;
    // fall back to all schedules if there's no "Main" (e.g. custom-only names).
    setSelectedScheduleNames(
      avail.scheduleNames.includes("Main") ? ["Main"] : avail.scheduleNames
    );
  }, [show, guide]);

  const toggleSection = (id) =>
    setSections(prev => prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s));

  const toggleScheduleName = (name) =>
    setSelectedScheduleNames(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );

  const move = (i, dir) => setSections(prev => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const buildOptions = () => ({
    includeHero,
    includeStats,
    includeOverallMap,
    sections: sections.map(s => ({ id: s.id, enabled: s.enabled && s.available })),
    selectedScheduleNames,
  });

  const handleHTML = () => { onHide(); setTimeout(() => onExportHTML(buildOptions()), 150); };
  const handlePDF  = () => { onHide(); setTimeout(() => onExportPDF(buildOptions()),  150); };

  const scheduleSection = sections.find(s => s.id === "schedule");
  const showScheduleNames = scheduleNames.length > 1 && scheduleSection?.enabled && scheduleSection?.available;

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName="eo-dialog">
      <Modal.Body style={{ padding: 0 }}>
        <div className="eo-shell">

          {/* ── Header ── */}
          <div className="eo-header">
            <div className="eo-header-icon">
              <FontAwesomeIcon icon={solid("file-export")} />
            </div>
            <h2 className="eo-title">Export Options</h2>
            <button className="eo-close" onClick={onHide}>
              <FontAwesomeIcon icon={solid("xmark")} />
            </button>
          </div>

          {/* ── Body ── */}
          <div className="eo-body">

            {/* Fixed items */}
            <div className="eo-group-label">Always at top</div>
            <div className="eo-fixed-group">
              {[
                { label: "Cover / Hero",  checked: includeHero,       set: setIncludeHero,       always: true },
                { label: "Stats Bar",     checked: includeStats,       set: setIncludeStats,       always: false },
                { label: "Overview Map",  checked: includeOverallMap,  set: setIncludeOverallMap,  always: false,
                  disabled: !guideAvailability(guide).overallMap },
              ].map(item => (
                <label key={item.label} className={`eo-fixed-row${item.disabled ? " eo-unavailable" : ""}`}>
                  <input
                    type="checkbox"
                    className="eo-cb"
                    checked={item.checked && !item.disabled}
                    disabled={item.disabled}
                    onChange={e => item.set(e.target.checked)}
                  />
                  <span className="eo-row-label">{item.label}</span>
                  {item.disabled && <span className="eo-badge">empty</span>}
                </label>
              ))}
            </div>

            <div className="eo-divider" />

            {/* Reorderable sections */}
            <div className="eo-group-label">
              Sections
              <span className="eo-hint"> — use arrows to reorder</span>
            </div>
            <div className="eo-sections">
              {sections.map((s, i) => (
                <React.Fragment key={s.id}>
                  <div className={`eo-section-row${!s.available ? " eo-unavailable" : ""}`}>
                    <input
                      type="checkbox"
                      className="eo-cb"
                      checked={s.enabled && s.available}
                      disabled={!s.available}
                      onChange={() => toggleSection(s.id)}
                    />
                    <span className="eo-row-label">{s.label}</span>
                    {!s.available && <span className="eo-badge">empty</span>}
                    <div className="eo-arrows">
                      <button
                        className="eo-arrow-btn"
                        onClick={() => move(i, -1)}
                        disabled={i === 0}
                        title="Move up"
                      >
                        <FontAwesomeIcon icon={solid("chevron-up")} />
                      </button>
                      <button
                        className="eo-arrow-btn"
                        onClick={() => move(i, 1)}
                        disabled={i === sections.length - 1}
                        title="Move down"
                      >
                        <FontAwesomeIcon icon={solid("chevron-down")} />
                      </button>
                    </div>
                  </div>
                  {s.id === "schedule" && showScheduleNames && (
                    <div className="eo-schedule-names">
                      {scheduleNames.map(name => (
                        <label key={name} className="eo-schedule-name-row">
                          <input
                            type="checkbox"
                            className="eo-cb"
                            checked={selectedScheduleNames.includes(name)}
                            onChange={() => toggleScheduleName(name)}
                          />
                          <span className="eo-row-label">{name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="eo-footer">
            <button className="eo-cancel" onClick={onHide}>Cancel</button>
            <button className="eo-btn eo-btn-html" onClick={handleHTML}>
              <FontAwesomeIcon icon={solid("file-code")} />
              HTML
            </button>
            <button className="eo-btn eo-btn-pdf" onClick={handlePDF}>
              <FontAwesomeIcon icon={solid("file-pdf")} />
              PDF
            </button>
          </div>

        </div>
      </Modal.Body>
    </Modal>
  );
}
